/**
 * Component for handling plan selection and pricing display
 * @author Housefolios
 * @modifiedBy David Helmick
 * @modifiedTime 2025-06-17 13:50:00
 */

import React from 'react'
import {
  Grid, Button, ButtonGroup, Card, CardActions, CardContent, CardHeader,  Typography, List, ListItem, Divider } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Check } from '@mui/icons-material'
// Centralized lodash imports - much cleaner!
import { includes, map, isEmpty, filter } from '@/utils/lodash'
import { btnOutlinePrimaryTabSx } from '@/theme/skinSx'
import PlanSelectionCard from './PlanSelectionCard'

import moneyBackGuarantee from '@/assets/images/moneyBackGuarantee.png'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import bgNewLarge from '@/assets/images/bgNewLarge.png'

import { Link as RouterLink } from 'react-router'

const PlanSelection = ({
  setSelectedPlan,
  setPaymentIntervals,
  currencyFormat,
  memberTrial,
  activeTab,
  tabSwitch2,
  setTab,
  allowedPlans,
  pricingPlans,
  trialInvoiceAmount,
  source }) => {
  const handlePlanChange = (plan) => {
    setSelectedPlan(plan)
  }

  const handlePaymentIntervalChange = (interval) => {
    setPaymentIntervals(interval)
  }

  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 })

  return (
    <Card
      style={{
        padding: 30,
        marginLeft: 15,
        marginRight: 15,
        marginBottom: 15,
        maxWidth: 1200}}
    >
      <CardContent>
        <Grid container direction="column">
          <Grid size={12}>
            <Typography
              variant="h5"
              style={{
                fontWeight: 600}}
            >
              Select Plan
            </Typography>
          </Grid>
          <Grid
            style={{
              textAlign: 'center',
              marginBottom: 15,
              marginTop: 25}}
          >
            <ButtonGroup size="small" variant="text">
              <Button
                style={{
                  maxWidth: 200}}
                onClick={() => {
                  tabSwitch2('Annual')
                }}
                sx={btnOutlinePrimaryTabSx(activeTab === 'Annual')}
              >
                <LocalOfferIcon
                  style={{
                    paddingRight: 5}}
                />
                Annual
              </Button>
              <Button
                style={{
                  maxWidth: 200}}
                onClick={() => {
                  tabSwitch2('Monthly')
                }}
                sx={btnOutlinePrimaryTabSx(activeTab === 'Monthly')}
              >
                <CalendarTodayIcon
                  style={{
                    paddingRight: 5}}
                />
                Monthly
              </Button>
            </ButtonGroup>
          </Grid>
          {Boolean(trialInvoiceAmount) ? (
            <Grid size={12} style={{ padding: 50 }}>
              <Typography>
                *Try the site for ${trialInvoiceAmount} for the first{' '}
                {memberTrial} days. After your trial is completed,
                plans start at just $49 per month.
              </Typography>
            </Grid>
          ) : (
            <Grid size={12} style={{ padding: 50 }}>
              <Typography>
                *Try the site free for the first {memberTrial} days.
                After your trial is completed, plans start at just $49
                per month.
              </Typography>
            </Grid>
          )}
          <Grid size={12}>
            <Grid
              container
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "center"
              }}
            >
              {map(
                !isEmpty(allowedPlans)
                  ? filter(pricingPlans, (section) =>
                      includes(
                        allowedPlans,
                        activeTab === 'Monthly'
                          ? section.nameMonthly
                          : section.nameYearly,
                      ),
                    )
                  : pricingPlans,
                (section) => (
                  <PlanSelectionCard
                    key={
                      activeTab === 'Monthly'
                        ? section.nameMonthly
                        : section.nameYearly
                    }
                    section={section}
                    activeTab={activeTab}
                    currencyFormat={currencyFormat}
                    setSelectedPlan={setSelectedPlan}
                    setTab={setTab}
                  />
                ),
              )}
            </Grid>
            {!Boolean(memberTrial) && (
              <Grid size={12} style={{ padding: 50 }}>
                <Grid
                  container
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center"
                  }}
                >
                  <Grid size={{sm: 3}}>
                    <img
                      src={moneyBackGuarantee}
                      style={{
                        maxWidth: 225}}
                    />
                  </Grid>
                  <Grid size={{sm: 9}}>
                    <Grid container direction="column" spacing={2}>
                      <Grid>
                        <Typography variant="h4">
                          What do you have to lose
                        </Typography>
                      </Grid>
                      <Grid>
                        <Typography>
                          We use Housefolios to help us with our own
                          investing. We are confident that it will
                          work for you too! However, if for any reason
                          you are not satisfied within 30 days, you
                          can get a full refund.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {!source && (
              <Grid size={12}
                style={{
                  borderStyle: 'solid',
                  borderWidth: 'thick',
                  borderColor: 'lightGrey',
                  borderRadius: 5,
                  marginBottom: 20,
                  paddingBottom: 25,
                  paddingLeft: 25,
                  paddingTop: 25,
                  paddingRight: 5}}
              >
                <Grid container direction="row" sx={{
                  alignItems: "center"
                }}>
                  <Grid size={{sm: 9}}>
                    <Grid container direction="column">
                      <Grid>
                        <Typography variant="h5">
                          Enterprise Plans
                        </Typography>
                      </Grid>
                      <Grid>
                        <Typography>
                          Are the plans outlined above not enough for
                          your organization? Do you need to analyze
                          more than 1,000 properties each month? Or do
                          you just want to visit with someone and get
                          a walkthrough of the platform before making
                          a decision to purchase?
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{sm: 3}}
                    style={{
                      textAlign: 'center'}}
                  >
                    <Button
                      variant="contained"
                      disabled
                      color="primary"
                      component={RouterLink}
                      to="/support-chat"
                      onClick={() => navigate('/support-chat')}
                      style={{ margin: '8px' }}
                    >
                      <Typography>Contact Us</Typography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default PlanSelection
